import { Mesh, Triangle, Vector3, type Object3D } from 'three';
import { Octree } from 'three/examples/jsm/math/Octree.js';

/**
 * Build an Octree from the live house scene graph for capsule vs. world
 * collision in ego mode.
 *
 * Inclusion rules:
 *
 *   1. Visible meshes contribute their triangles, UNLESS they live
 *      inside a `Hinge_*` group (door slabs, window sashes, gate
 *      panels), have `userData.collisionIgnore` (individual stair
 *      treads — the ramp proxy carries their collision instead), or
 *      are hidden by a non-ego ancestor (story peel, "house only"
 *      toggle, etc.).
 *   2. INVISIBLE meshes with `userData.collisionProxy` set DO
 *      contribute — this is how hidden slope meshes under each stair
 *      flight stand in for the visible treads so the capsule slides
 *      smoothly up without the step-up solver fighting each riser.
 *
 * The hinge-exclusion design sidesteps three classes of bugs that
 * plagued the earlier threshold-based approach:
 *
 *   - Rebuilding the octree mid-key-press (when the user E-toggles a
 *     hinge) was heavy enough that the browser would sometimes drop
 *     pointer lock — flipping us out of ego mode with panels
 *     returning to full opacity (user-reported "white modal").
 *   - A closed-leaf mesh suddenly appearing in the octree could push
 *     the capsule with a bad normal (e.g., downward at a window sill)
 *     and dump the player off the plinth.
 *   - Animating hinge rotations while also asserting collision at
 *     every tween frame would require per-frame rebuilds — too
 *     expensive, and any tween that passed through the player's
 *     capsule would fight the physics solver.
 *
 * With leaves always excluded, the octree is stable across every
 * hinge change and only needs to be rebuilt when the house itself is
 * regenerated (or when ego mode is first entered).
 */
export function buildCollisionOctree(root: Object3D): Octree {
  const octree = new Octree();
  root.updateWorldMatrix(true, true);

  root.traverse((obj) => {
    if (!(obj instanceof Mesh)) return;
    if (!obj.geometry) return;
    const isProxy = obj.userData?.collisionProxy === true;
    // Individual stair treads, if generated, are visual-only; their
    // ramp proxy carries the collision.
    if (obj.userData?.collisionIgnore === true) return;
    // Proxies are allowed to be hidden; everything else must be
    // visible up the entire ancestor chain.
    if (!isProxy && !obj.visible) return;
    let ancestor: Object3D | null = obj.parent;
    let skip = false;
    let hiddenByAncestor = false;
    while (ancestor) {
      if (!ancestor.visible) hiddenByAncestor = true;
      if (ancestor.name?.startsWith('Hinge_')) {
        skip = true;
        break;
      }
      ancestor = ancestor.parent;
    }
    if (skip) return;
    // "Hidden by ancestor" still excludes proxies too — if the user
    // toggles a whole story off in peel view, the ramps under it
    // should vanish from collision as well; otherwise the player
    // would glide up a ramp to a non-existent floor.
    if (hiddenByAncestor) return;

    const geometry = obj.geometry;
    let geo = geometry;
    let isTemp = false;
    if (geometry.index !== null) {
      geo = geometry.toNonIndexed();
      isTemp = true;
    }
    const position = geo.getAttribute('position');
    if (!position) {
      if (isTemp) geo.dispose();
      return;
    }

    for (let i = 0; i < position.count; i += 3) {
      const v1 = new Vector3().fromBufferAttribute(position, i);
      const v2 = new Vector3().fromBufferAttribute(position, i + 1);
      const v3 = new Vector3().fromBufferAttribute(position, i + 2);
      v1.applyMatrix4(obj.matrixWorld);
      v2.applyMatrix4(obj.matrixWorld);
      v3.applyMatrix4(obj.matrixWorld);
      octree.addTriangle(new Triangle(v1, v2, v3));
    }

    if (isTemp) geo.dispose();
  });

  // `build()` computes the enclosing box from the accumulated triangles
  // and recursively subdivides. Skip if no triangles (no visible house).
  if (octree.triangles.length > 0) {
    octree.build();
  }

  return octree;
}
